import { getAppHost, isAppHostResolved } from "../../lib/appHost";
import { Button } from "@/components/ui/button";
import { AmsaasClientLoginPage } from "./amsaasClient";
import { AmsaasProviderLoginPage } from "./amsaasProvider";
import { AmcaasPlatformLoginPage } from "./amcaasPlatform";
import { AmcaasProviderLoginPage } from "./amcaasProvider";

/**
 * Host IS a trust boundary (AUTH-06): `amcaas.app` is the B2C app, everything
 * else (incl. `amsaas.app` and local dev) is the B2B app. Two doors share the
 * `/login/provider` path across the two hosts, so the host selects the variant
 * at runtime.
 *
 * Delegates to the single, sessionStorage-backed `getAppHost()` so the dev
 * `?app=amcaas` / `?app=amsaas` override is **sticky** across navigations (the
 * query param is captured once at module load, not re-read from each URL). Using
 * a divergent URL-only check here previously made `/login` fall back to the B2B
 * door once the param dropped off the URL.
 */
export function isAmcaasHost(): boolean {
  if (typeof window === "undefined") return false;
  return getAppHost() === "b2c";
}

/** `/login/provider` — same console, picked by host (AUTH-10, TOP-06). */
export function ProviderLoginPage(): JSX.Element {
  return isAmcaasHost() ? (
    <AmcaasProviderLoginPage />
  ) : (
    <AmsaasProviderLoginPage />
  );
}

/** `/login` — bare door falls back to the host's primary audience. */
export function DefaultLoginPage(): JSX.Element {
  return isAmcaasHost() ? (
    <AmcaasPlatformLoginPage />
  ) : (
    <AmsaasClientLoginPage />
  );
}

/**
 * Gate every login door on an explicitly-resolved app host. On a real `amcaas*`/
 * `amsaas*` host this is transparent; on a bare `localhost` with no `?app=` override
 * the app is ambiguous, so we force the operator to choose rather than silently show
 * the B2B door.
 */
export function AppHostGate({
  children,
}: {
  children: JSX.Element;
}): JSX.Element {
  if (isAppHostResolved()) return children;
  return <ChooseAppNotice />;
}

function ChooseAppNotice(): JSX.Element {
  const path =
    typeof window !== "undefined" ? window.location.pathname : "/login";
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 p-10 text-center">
      <img
        src="/Logo.svg"
        alt="AMSaaS"
        className="w-40 max-w-full object-contain"
      />
      <h1 className="text-lg font-semibold">Choose an app to sign in</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        On this dev host the app is ambiguous. Pick the business app (AMSaaS) or
        the marketplace app (AMCaaS) — in production the domain decides
        automatically.
      </p>
      <div className="flex gap-2">
        <Button asChild>
          <a href={`${path}?app=amsaas`}>Business app (AMSaaS)</a>
        </Button>
        <Button asChild variant="outline">
          <a href={`${path}?app=amcaas`}>Marketplace app (AMCaaS)</a>
        </Button>
      </div>
    </div>
  );
}
